import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';

import { APP_PATH, appUrl } from '@core/constants/app-paths';
import { nextFromUrl } from '@core/guards/membership-session.guard';
import { MembershipSummary } from '@core/models/membership.model';
import { MembershipGrowthContextService } from '@core/services/membership-growth-context.service';
import { MembershipSessionService } from '@core/services/membership-session.service';

/**
 * The one place on the site where a member signs in, sees who they are signed in AS, and signs out.
 *
 * Owner, 2026-09-18: *"sign in option should be one time and it is shown on the top right corner
 * with profile avtar like most of the portals than no need to sign in process in every page…
 * user experience should be as simple as possible so that any layman person also can use the
 * application."*
 *
 * ⚠ IT OWNS NO SESSION OF ITS OWN. It renders `MembershipSessionService`, which every screen
 * already reads, so there is exactly ONE session and this control is a view of it (D-D6). Signing
 * in still happens on `/verify` — the difference is that it is reached from here once, instead of
 * being asked for again by each page in its own body.
 *
 * ⚠ THE SESSION IS PER BROWSER TAB AND IS LEFT THAT WAY, DELIBERATELY (D-D6). The token is a
 * 30-minute credential on a public page that gets opened on shared and family phones, and
 * `MembershipSessionService`'s own header says so. Moving it to `localStorage` would make one
 * sign-in follow the member into every tab — which is what "sign in once" sounds like it asks for,
 * and it would also leave a working credential behind on a borrowed phone. "Once" here means once
 * per visit, from one control, not "for ever on this device".
 */
@Component({
  selector: 'lm-account-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './account-menu.component.html',
  styleUrls: ['./account-menu.component.scss'],
  imports: [RouterLink]
})
export class AccountMenuComponent implements OnInit {
  private readonly session = inject(MembershipSessionService);
  private readonly growth = inject(MembershipGrowthContextService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  summary: MembershipSummary | null = null;
  signedIn = false;
  open = false;

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.session.token$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((token) => {
      this.signedIn = !!token;
      if (!token) {
        this.open = false;
      }
      this.cdr.markForCheck();
    });

    this.session.summary$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((summary) => {
      this.summary = summary;
      this.cdr.markForCheck();
    });

    // A menu left hanging open across a navigation is the classic way one of these misbehaves.
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.open = false;
        this.cdr.markForCheck();
      });
  }

  /** What to call them. The name when the server has sent one, otherwise the masked number. */
  get label(): string {
    const name = (this.summary?.holderName || '').trim();
    if (name) {
      return name;
    }
    return (this.summary?.maskedPhone || '').trim() || 'My account';
  }

  /**
   * The avatar. Initials where there is a name to take them from — never a made-up letter, and
   * never a photograph, because Lifemaan holds none.
   */
  get initials(): string {
    const parts = (this.summary?.holderName || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '';
    }
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  }

  toggle(): void {
    this.open = !this.open;
    this.cdr.markForCheck();
  }

  /**
   * ⚠ SIGN IN RETURNS THEM TO WHERE THEY WERE (D-D7). The member pressed this in the middle of
   * something; dropping them on the home page afterwards is the thing the owner is complaining
   * about. `nextFromUrl` is the guard's OWN function, reused rather than re-derived, so the control
   * and the guard can never disagree about where "back" is.
   */
  signIn(): void {
    this.open = false;
    void this.router.navigate([appUrl(APP_PATH.VERIFY)], { queryParams: { next: nextFromUrl(this.router.url) } });
  }

  /**
   * Signing out forgets everything this tab kept for the member: the session and its summary, the referral link they arrived
   * with and the city they chose (review MR !1 N25) - on a shared or family phone the next person must not inherit any of it.
   */
  signOut(): void {
    this.open = false;
    this.session.clear();
    this.growth.clear();
    void this.router.navigate([appUrl(APP_PATH.PLANS)]);
  }

  /** Clicking anywhere else closes it — and this component's own host is not "anywhere else". */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open) {
      return;
    }
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) {
      this.open = false;
      this.cdr.markForCheck();
    }
  }
}
